import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Deal } from "../types";
import { useRefreshFunnel } from "./hooks";

type Money = Pick<Deal, "id"> & {
  amount?: number | null;
  paid_amount?: number | null;
  paid_at?: string | null;
  cost_amount?: number | null;
};

const num = (value: string) => {
  const n = Number(value.replace(/\s/g, "").replace(",", "."));
  return value.trim() === "" || !Number.isFinite(n) ? null : Math.round(n);
};
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Money of a deal. mode "won": the sale amount is required to close the deal;
 * "paid": the payment and its date; "edit": just the numbers.
 */
export const SaleDialog = ({
  deal,
  mode,
  open,
  onOpenChange,
  onSaved,
}: {
  deal: Money;
  mode: "won" | "paid" | "edit";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`crm.funnel.sale.${key}`);
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefreshFunnel();
  const { currency } = useConfigurationContext();
  const [amount, setAmount] = useState("");
  const [paid, setPaid] = useState("");
  const [paidAt, setPaidAt] = useState(today());
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setAmount(deal.amount ? String(deal.amount) : "");
    setPaid(
      deal.paid_amount
        ? String(deal.paid_amount)
        : mode === "paid" && deal.amount
          ? String(deal.amount)
          : "",
    );
    setPaidAt(deal.paid_at ? deal.paid_at.slice(0, 10) : today());
    setCost(deal.cost_amount ? String(deal.cost_amount) : "");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const amountValue = num(amount);
  const paidValue = num(paid);
  const invalid =
    ((mode === "won" || mode === "edit") && !amountValue) ||
    (mode === "paid" && (!paidValue || !paidAt));

  const submit = async () => {
    setSaving(true);
    try {
      await dataProvider.update("deals", {
        id: deal.id,
        data: {
          amount: amountValue,
          cost_amount: num(cost),
          ...(mode === "won" ? { stage: "won" } : {}),
          ...(paidValue
            ? {
                paid_amount: paidValue,
                paid_at: new Date(`${paidAt}T12:00:00`).toISOString(),
              }
            : mode === "edit"
              ? { paid_amount: null, paid_at: null }
              : {}),
        },
        previousData: deal,
      });
      notify(t(mode === "won" ? "done_won" : "saved"));
      await refresh();
      onOpenChange(false);
      onSaved?.();
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const field =
    "flex flex-col gap-1 text-xs font-semibold text-muted-foreground";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>{t(`title_${mode}`)}</DialogTitle>
        <DialogDescription>{t(`hint_${mode}`)}</DialogDescription>
        <div className="grid gap-3">
          {mode !== "paid" ? (
            <label className={field}>
              {t("amount")} ({currency}) *
              <Input
                inputMode="numeric"
                autoFocus
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <label className={field}>
              {t("paid")} ({currency}){mode === "paid" ? " *" : ""}
              <Input
                inputMode="numeric"
                autoFocus={mode === "paid"}
                value={paid}
                onChange={(e) => setPaid(e.target.value)}
              />
            </label>
            <label className={field}>
              {t("paid_at")}
              <Input
                type="date"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
              />
            </label>
          </div>
          {mode !== "paid" ? (
            <label className={field}>
              {t("cost")} ({currency})
              <Input
                inputMode="numeric"
                value={cost}
                placeholder={t("cost_hint")}
                onChange={(e) => setCost(e.target.value)}
              />
            </label>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            {translate("ra.action.cancel")}
          </Button>
          <Button type="button" disabled={saving || invalid} onClick={submit}>
            {t(mode === "won" ? "submit_won" : "submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
